import { useCallback, useEffect, useState } from "react";
import { activeLeadMinutes, DEFAULT_LEADS, validateAlertConfig, type AlertConfig, type AlertLead } from "../../core/alerts";
import { defaultOriginFor } from "../../shared/origins";
import type { CommuteSnapshot } from "../../shared/types";
import { primeVoice } from "../voice";
import { isNativeShell, postAskNotifications } from "../native";
import { TravelScheduleEditor } from "./TravelSchedule";
import type { useTravelSchedule } from "../useTravelSchedule";
import { useSheetDismiss } from "../useSheetDismiss";
import { ScrollTopButton } from "./ScrollTopButton";
import type { NativePushToken } from "../../shared/native-bridge";
import type { Preferences } from "../usePreferences";

const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const RESET_PROFILE_ID = "civic-am";

const DEFAULT_CONFIG: AlertConfig = {
	enabled: true,
	profileId: "civic-am",
	targetMinutes: 8 * 60 + 10,
	days: [0, 1, 2, 3, 4],
	leads: DEFAULT_LEADS,
	trainAlerts: true,
	soundEnabled: true,
	origin: null,
	earlyToleranceMinutes: null,
	lateToleranceMinutes: null,
	egressSeconds: null,
	prepSeconds: null,
	stationBufferSeconds: null,
};

interface ServerState {
	vapidPublicKey: string | null;
	configured: boolean;
	alertableProfileIds: string[];
	subscription: { config: AlertConfig; updatedAtMs: number } | null;
}

type Status = { kind: "idle" | "saving" | "saved" | "error" | "testing"; message?: string };

function urlBase64ToUint8Array(value: string): Uint8Array {
	const padded = `${value}${"=".repeat((4 - (value.length % 4)) % 4)}`.replace(/-/g, "+").replace(/_/g, "/");
	const binary = atob(padded);
	return Uint8Array.from([...binary].map((char) => char.charCodeAt(0)));
}

export function AlertsSheet({
	travelSchedule,
	snapshot,
	deviceId,
	preferredProfileId,
	preferredTargetMinutes,
	soundEnabled,
	prepSeconds,
	stationBufferSeconds,
	earlyToleranceMinutes,
	lateToleranceMinutes,
	egressSeconds,
	push,
	onTimingChange,
	onDefaultsChange,
	onEnabledChange,
	onSaved,
	onClose,
}: {
	travelSchedule: ReturnType<typeof useTravelSchedule>;
	snapshot: CommuteSnapshot | null;
	deviceId: string;
	preferredProfileId: string | null;
	preferredTargetMinutes: number | null;
	/** In-app chimes and spoken reminders. iOS controls notification sound independently. */
	soundEnabled: boolean;
	/**
	 * User timing overrides. The cron needs them or it computes a schedule the card never showed.
	 * Null means the selected profile's own value.
	 */
	prepSeconds: number | null;
	stationBufferSeconds: number | null;
	earlyToleranceMinutes: number | null;
	lateToleranceMinutes: number | null;
	egressSeconds: number | null;
	/** The shell's APNs token, when there is one. Null in a browser, where push works the old way. */
	push: NativePushToken | null;
	onTimingChange: (patch: Partial<Pick<Preferences, "earlyToleranceMinutes" | "lateToleranceMinutes" | "egressSeconds" | "prepSeconds" | "stationBufferSeconds">>) => void;
	onDefaultsChange: (defaults: {
		profileId: string;
		targetMinutes: number;
		days: number[];
		origin: { lat: number; lon: number } | null;
		/** The reminders now in force, which the spoken alerts keep in step with. */
		leads: number[];
		earlyToleranceMinutes: number | null;
		lateToleranceMinutes: number | null;
		egressSeconds: number | null;
		prepSeconds: number | null;
		stationBufferSeconds: number | null;
	}) => void;
	onEnabledChange: (enabled: boolean) => void;
	/** Fires once the server has the new settings, so the view can re-plan against them. */
	onSaved: () => void;
	onClose: () => void;
}) {
	const [server, setServer] = useState<ServerState | null>(null);
	const [config, setConfig] = useState<AlertConfig>(() => ({
		...DEFAULT_CONFIG,
		profileId: preferredProfileId ?? DEFAULT_CONFIG.profileId,
		targetMinutes: preferredTargetMinutes ?? DEFAULT_CONFIG.targetMinutes,
		earlyToleranceMinutes,
		lateToleranceMinutes,
		egressSeconds,
		prepSeconds,
		stationBufferSeconds,
	}));
	const [savedConfig, setSavedConfig] = useState<AlertConfig | null>(null);
	const [status, setStatus] = useState<Status>({ kind: "idle" });
	const [permission, setPermission] = useState<NotificationPermission>(
		typeof Notification === "undefined" ? "default" : Notification.permission,
	);
	const dismiss = useSheetDismiss(onClose);

	const deadlineProfile = snapshot?.profiles.find((entry) => entry.hasTargetWindow);
	const finalLabel = deadlineProfile?.finalLabel ?? "school";
	const early = config.earlyToleranceMinutes ?? 6;
	const late = config.lateToleranceMinutes ?? 13;
	const egress = config.egressSeconds ?? deadlineProfile?.egressSeconds ?? 0;
	const prep = config.prepSeconds ?? deadlineProfile?.prepSeconds ?? 300;
	const buffer = config.stationBufferSeconds ?? deadlineProfile?.stationBufferSeconds ?? 420;


	useEffect(() => {
		void fetch(`/api/alerts?deviceId=${deviceId}`)
			.then((response) => {
				if (!response.ok) {
					throw new Error("Could not load alert settings.");
				}
				return response.json() as Promise<ServerState>;
			})
			.then((state) => {
				setServer(state);
				if (state.subscription) {
					const storedConfig = validateAlertConfig(state.subscription.config, state.alertableProfileIds);
					if (!storedConfig) {
						throw new Error("Saved alert settings are invalid.");
					}
					const resolved = {
						...storedConfig,
						profileId: preferredProfileId ?? storedConfig.profileId,
						targetMinutes: preferredTargetMinutes ?? storedConfig.targetMinutes,
						earlyToleranceMinutes: earlyToleranceMinutes ?? storedConfig.earlyToleranceMinutes,
						lateToleranceMinutes: lateToleranceMinutes ?? storedConfig.lateToleranceMinutes,
						egressSeconds: egressSeconds ?? storedConfig.egressSeconds,
						prepSeconds: prepSeconds ?? storedConfig.prepSeconds,
						stationBufferSeconds: stationBufferSeconds ?? storedConfig.stationBufferSeconds,
					};
					setConfig(resolved);
					setSavedConfig(storedConfig);
					onDefaultsChange({
						profileId: resolved.profileId,
						targetMinutes: resolved.targetMinutes,
						days: resolved.days,
						origin: resolved.origin,
						leads: activeLeadMinutes(resolved),
						earlyToleranceMinutes: resolved.earlyToleranceMinutes,
						lateToleranceMinutes: resolved.lateToleranceMinutes,
						egressSeconds: resolved.egressSeconds,
						prepSeconds: resolved.prepSeconds,
						stationBufferSeconds: resolved.stationBufferSeconds,
					});
				} else {
					onDefaultsChange({
						profileId: config.profileId,
						targetMinutes: config.targetMinutes,
						days: config.days,
						origin: config.origin,
						leads: activeLeadMinutes(config),
						earlyToleranceMinutes: config.earlyToleranceMinutes,
						lateToleranceMinutes: config.lateToleranceMinutes,
						egressSeconds: config.egressSeconds,
						prepSeconds: config.prepSeconds,
						stationBufferSeconds: config.stationBufferSeconds,
					});
				}
				onEnabledChange(state.subscription?.config.enabled === true);
			})
			.catch(() => setStatus({ kind: "error", message: "Could not load alert settings." }));
	}, [deviceId, onEnabledChange]);

	const dirty = savedConfig == null || JSON.stringify(savedConfig) !== JSON.stringify(config);
	// The app reports itself as a browser tab, not standalone, and has its own alert channel. Both
	// warnings below are about a browser's limits and mean nothing here.
	const native = isNativeShell();
	const standalone = typeof window !== "undefined" && window.matchMedia("(display-mode: standalone)").matches;

	const save = useCallback(async () => {
		setStatus({ kind: "saving" });
		primeVoice();
		try {
			const enabledConfig = { ...config, enabled: true, soundEnabled };

			/*
			 * Inside the shell there is no subscription to make.
			 *
			 * `pushManager.subscribe` is Safari and Home-Screen web apps only — in a `WKWebView` it
			 * either throws or hands back something Apple will never deliver to. The APNs token the
			 * shell registered for takes its place, and everything after this point is identical.
			 *
			 * Keyed on being in the app rather than on holding a token: the shell says the token over
			 * the bridge, and a page that reloaded before it said it again has none. Sending nothing
			 * leaves the one already on file, which is the same token.
			 */
			if (native) {
				// Nothing here waits on the answer: permission decides whether the push is *seen*, and a
				// rider who says no still wants the reminder settings saved.
				postAskNotifications();
				const registered = await fetch("/api/alerts", {
					method: "PUT",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(push ? { deviceId, config: enabledConfig, apns: push } : { deviceId, config: enabledConfig }),
				});
				if (!registered.ok) {
					const body = (await registered.json().catch(() => null)) as { error?: { message?: string } } | null;
					throw new Error(body?.error?.message ?? "Could not save the alert.");
				}
				setConfig(enabledConfig);
				setSavedConfig(enabledConfig);
				onDefaultsChange({
					profileId: enabledConfig.profileId,
					targetMinutes: enabledConfig.targetMinutes,
					days: enabledConfig.days,
					origin: enabledConfig.origin,
					leads: activeLeadMinutes(enabledConfig),
					earlyToleranceMinutes: enabledConfig.earlyToleranceMinutes,
					lateToleranceMinutes: enabledConfig.lateToleranceMinutes,
					egressSeconds: enabledConfig.egressSeconds,
					prepSeconds: enabledConfig.prepSeconds,
					stationBufferSeconds: enabledConfig.stationBufferSeconds,
				});
				onEnabledChange(true);
				onSaved();
				setStatus({ kind: "saved" });
				return;
			}

			if (typeof Notification === "undefined" || !("serviceWorker" in navigator)) {
				throw new Error("This browser cannot show notifications.");
			}
			const granted = await Notification.requestPermission();
			setPermission(granted);
			if (granted !== "granted") {
				throw new Error("Notifications are blocked. Allow them in iOS Settings → Notifications → GoTime.");
			}
			if (!server?.vapidPublicKey) {
				throw new Error("Push is not configured on the server.");
			}

			// Waiting on the worker with no deadline means a failure to start looks like a save
			// that never finishes, which is the worst way to report anything.
			const registration = await Promise.race([
				navigator.serviceWorker.ready,
				new Promise<never>((_, reject) =>
					setTimeout(() => reject(new Error("The background worker did not start. Reload GoTime and try again.")), 10_000),
				),
			]);
			const existing = await registration.pushManager.getSubscription();
			const subscription =
				existing ??
				(await registration.pushManager.subscribe({
					userVisibleOnly: true,
					applicationServerKey: urlBase64ToUint8Array(server.vapidPublicKey) as BufferSource,
				}));

			const response = await fetch("/api/alerts", {
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ deviceId, config: enabledConfig, subscription: subscription.toJSON() }),
			});
			if (!response.ok) {
				const body = (await response.json().catch(() => null)) as { error?: { message?: string } } | null;
				throw new Error(body?.error?.message ?? "Could not save the alert.");
			}

			setConfig(enabledConfig);
			setSavedConfig(enabledConfig);
			// Saving is what applies: until now the app has carried on with the old settings, so
			// the alerts and the view can never describe different commutes.
			onDefaultsChange({
				profileId: enabledConfig.profileId,
				targetMinutes: enabledConfig.targetMinutes,
				days: enabledConfig.days,
				origin: enabledConfig.origin,
				leads: activeLeadMinutes(enabledConfig),
				earlyToleranceMinutes: enabledConfig.earlyToleranceMinutes,
				lateToleranceMinutes: enabledConfig.lateToleranceMinutes,
				egressSeconds: enabledConfig.egressSeconds,
				prepSeconds: enabledConfig.prepSeconds,
				stationBufferSeconds: enabledConfig.stationBufferSeconds,
			});
			onEnabledChange(true);
			onSaved();
			setStatus({ kind: "saved" });
		} catch (cause) {
			setStatus({ kind: "error", message: cause instanceof Error ? cause.message : "Could not save the alert." });
		}
	}, [config, deviceId, native, onDefaultsChange, onEnabledChange, onSaved, push, server, soundEnabled]);

	const disable = useCallback(async () => {
		await fetch(`/api/alerts?deviceId=${deviceId}`, { method: "DELETE" });
		setSavedConfig(null);
		setConfig((current) => ({ ...current, enabled: false }));
		onEnabledChange(false);
		setStatus({ kind: "saved", message: "Alerts turned off." });
	}, [deviceId, onEnabledChange]);

	const updateConfig = useCallback((patch: Partial<Pick<AlertConfig, "profileId" | "targetMinutes" | "days" | "origin" | "earlyToleranceMinutes" | "lateToleranceMinutes" | "egressSeconds" | "prepSeconds" | "stationBufferSeconds">>) => {
		setConfig((current) => ({ ...current, ...patch }));
	}, []);



	const leadRows = padLeads(config.leads);
	const resetToDefaults = useCallback(() => {
		const origin = defaultOriginFor(RESET_PROFILE_ID);
		setConfig({
			...DEFAULT_CONFIG,
			profileId: RESET_PROFILE_ID,
			targetMinutes: 8 * 60 + 5,
			origin: origin ? { lat: origin.lat, lon: origin.lon } : null,
			earlyToleranceMinutes: 6,
			lateToleranceMinutes: 13,
			egressSeconds: 7 * 60,
			prepSeconds: 2 * 60,
			stationBufferSeconds: 5 * 60,
		});
		setStatus({ kind: "idle", message: "Defaults ready. Save to apply them." });
	}, []);

	const setLead = useCallback((index: number, patch: Partial<AlertLead>) => {
		setConfig((current) => {
			const rows = padLeads(current.leads);
			rows[index] = { ...rows[index], ...patch };
			return { ...current, leads: rows };
		});
	}, []);

	return (
		<div className="sheet" role="dialog" aria-label="Alerts">
			<div className="sheet__scrim" onClick={onClose} />
			<div
				className="sheet__body"
				ref={dismiss.bodyRef}
				style={dismiss.style}
				{...dismiss.handlers}
			>
				<span className="sheet__grip" aria-hidden="true" />
				<ScrollTopButton targetRef={dismiss.bodyRef} className="sheet__toTop" />
				<header className="sheet__head">
					<h2>Alerts</h2>
					<button type="button" onClick={onClose} aria-label="Close">
						✕
					</button>
				</header>

				{!standalone && !native ? (
					<p className="alerts__warn">
						iOS only delivers notifications to Home Screen apps. Open GoTime from its Home Screen icon before enabling alerts.
					</p>
				) : null}
				{server && !server.configured ? <p className="alerts__warn">Push is not configured on the server yet.</p> : null}
				{/* Not asked in the app: `Notification.permission` is always denied in a WKWebView, which
				    has no web notification API at all. The permission that matters there is iOS's own,
				    granted natively, and the page cannot read it. */}
				{permission === "denied" && !native ? (
					<p className="alerts__warn">Notifications are blocked for GoTime. Re-allow them in iOS Settings → Notifications.</p>
				) : null}

				<TravelScheduleEditor schedule={travelSchedule} />

				<section className="field">
					<h3>Arrive by</h3>
					<AlertTimeField minutes={config.targetMinutes} onChange={(targetMinutes) => updateConfig({ targetMinutes })} />
				</section>

				<section className="field">
					<h3>Morning timing</h3>
					<label className="sheet__stacked">
						<span>Okay this early — {early} min</span>
						<input type="range" min={0} max={40} step={1} value={early} onChange={(event) => {
							const value = Number(event.target.value);
							updateConfig({ earlyToleranceMinutes: value });
							onTimingChange({ earlyToleranceMinutes: value });
						}} />
					</label>
					<label className="sheet__stacked">
						<span>Tolerate this late — {late} min</span>
						<input type="range" min={0} max={20} step={1} value={late} onChange={(event) => {
							const value = Number(event.target.value);
							updateConfig({ lateToleranceMinutes: value });
							onTimingChange({ lateToleranceMinutes: value });
						}} />
					</label>
					<p className="field__hint">Window {clockLabel(config.targetMinutes - early)} to {clockLabel(config.targetMinutes + late)}</p>
					<label className="sheet__stacked">
						<span>Platform to {finalLabel} — {Math.round(egress / 60)} min</span>
						<input type="range" min={0} max={1200} step={30} value={egress} onChange={(event) => {
							const value = Number(event.target.value);
							updateConfig({ egressSeconds: value });
							onTimingChange({ egressSeconds: value });
						}} />
					</label>
					<label className="sheet__stacked">
						<span>Out the door — {Math.round(prep / 60)} min</span>
						<input
							type="range"
							min={0}
							max={900}
							step={30}
							value={prep}
							onChange={(event) => {
								const value = Number(event.target.value);
								updateConfig({ prepSeconds: value });
								onTimingChange({ prepSeconds: value });
							}}
						/>
					</label>
					<label className="sheet__stacked">
						<span>Station buffer — {Math.round(buffer / 60)} min</span>
						<input
							type="range"
							min={0}
							max={900}
							step={30}
							value={buffer}
							onChange={(event) => {
								const value = Number(event.target.value);
								updateConfig({ stationBufferSeconds: value });
								onTimingChange({ stationBufferSeconds: value });
							}}
						/>
					</label>
					<p className="field__hint">These allowances determine when GoTime tells you to leave.</p>
				</section>

				<section className="field">
					<h3>Push notifications</h3>
					<div className="alerts__leads">
						{leadRows.map((lead, index) => {
							const locked = lead.minutesBefore === 0;
							const on = locked || lead.enabled;
							return (
								<div key={index} className={`alerts__lead ${on ? "" : "is-off"}`}>
									<input
										type="checkbox"
										checked={on}
										disabled={locked}
										aria-label={`Reminder ${index + 1}`}
										onChange={(event) => setLead(index, { enabled: event.target.checked })}
									/>
									<select
										aria-label={`Reminder ${index + 1} time`}
										value={lead.minutesBefore}
										onChange={(event) => setLead(index, { minutesBefore: Number(event.target.value) })}
									>
										{LEAD_OPTIONS.map((option) => (
											<option
												key={option}
												value={option}
												disabled={leadRows.some((other, otherIndex) => otherIndex !== index && other.minutesBefore === option)}
											>
												{leadLabel(option)}
											</option>
										))}
									</select>
								</div>
							);
						})}
					</div>
					<p className="field__hint">The leave-time alert always sends; the earlier ones are yours to choose.</p>
				</section>
				{savedConfig ? (
					<button type="button" className="picker__reset alerts__turnOff" onClick={() => void disable()}>
						Turn off
					</button>
				) : null}
				<p className={`alerts__status ${status.kind === "error" ? "is-error" : ""}`}>
					{status.kind === "error"
						? status.message
						: status.message
							? status.message
							: savedConfig
								? dirty
									? "Unsaved changes."
									: `Alert saved for ${describeDays(travelSchedule.data?.days.flatMap((day, index) => day.am === null ? [] : [index]) ?? [])}.`
								: "No alert saved yet."}
				</p>
				<button type="button" className="picker__reset alerts__reset" onClick={resetToDefaults}>
					Reset to default settings
				</button>
				{/* Two thirds of this sheet is below the fold, so the action has to come to the reader. */}
				<div className={`alerts__save-bar ${dirty ? "" : "is-quiet"}`}>
					<button type="button" className={`alerts__save ${dirty ? "is-dirty" : ""}`} onClick={() => void save()} disabled={status.kind === "saving" || !dirty}>
						{status.kind === "saving" ? "Saving…" : dirty ? "Save alert" : "Saved"}
					</button>
				</div>
			</div>
		</div>
	);
}

function describeDays(days: number[]): string {
	if (days.length === 0) {
		return "no days";
	}
	if (days.length === 5 && days.every((day) => day < 5)) {
		return "weekdays";
	}
	return days.map((day) => DAY_NAMES[day].slice(0, 3)).join(", ");
}

/** Leave time, then every five minutes up to an hour before it. */
const LEAD_OPTIONS = Array.from({ length: 13 }, (_, index) => index * 5);
/** The sheet shows a fixed three, so the section does not change shape as they are edited. */
const LEAD_ROWS = 3;

function leadLabel(minutesBefore: number): string {
	return minutesBefore === 0 ? "At leave time" : `${minutesBefore} min before`;
}

function padLeads(leads: AlertLead[]): AlertLead[] {
	const rows = leads.slice(0, LEAD_ROWS);
	while (rows.length < LEAD_ROWS) {
		const used = new Set(rows.map((lead) => lead.minutesBefore));
		rows.push({ minutesBefore: LEAD_OPTIONS.find((option) => !used.has(option)) ?? 0, enabled: false });
	}
	return rows;
}

/** Every five minutes across the day, so "arrive by" is one choice rather than three. */
const TIME_STEPS = Array.from({ length: 24 * 12 }, (_, index) => index * 5);

function clockLabel(minutes: number): string {
	const hours24 = Math.floor(minutes / 60);
	const mins = minutes % 60;
	const hour12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
	return `${hour12}:${String(mins).padStart(2, "0")} ${hours24 < 12 ? "am" : "pm"}`;
}

function AlertTimeField({ minutes, onChange }: { minutes: number; onChange: (minutes: number) => void }) {
	const options = TIME_STEPS.includes(minutes) ? TIME_STEPS : [...TIME_STEPS, minutes].sort((a, b) => a - b);

	return (
		<select className="timefield__whole" aria-label="Arrive by" value={minutes} onChange={(event) => onChange(Number(event.target.value))}>
			{options.map((option) => (
				<option key={option} value={option}>
					{clockLabel(option)}
				</option>
			))}
		</select>
	);
}
